import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';

// Import shared components
import { H3, ListingLink, Toast } from '../../../../components';

// Import modules from this directory
import EditListingPhotosForm from './EditListingPhotosForm';
import css from './EditListingPhotosPanel.module.css';

const getInitialValues = params => {
  const { images = [] } = params;
  return { images };
};

/**
 * The EditListingPhotosPanel component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.errors - The errors object
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.ready - Whether the form is ready
 * @param {Array} props.images - The images array
 * @param {propTypes.ownListing} props.listing - The listing object
 * @param {Function} props.onImageUpload - The image upload function
 * @param {string} props.submitButtonText - The submit button text
 * @param {boolean} props.panelUpdated - Whether the panel is updated
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {Function} props.onSubmit - The submit function
 * @param {Function} props.onRemoveImage - The remove image function
 * @param {Object} props.listingImageConfig - The listing image config
 * @returns {JSX.Element}
 */
const EditListingPhotosPanel = props => {
  const intl = useIntl();
  const {
    className,
    rootClassName,
    errors,
    disabled,
    ready,
    listing,
    onImageUpload,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    onSubmit,
    onRemoveImage,
    listingImageConfig,
  } = props;

  // Toast state for save confirmation
  const [showSaveToast, setShowSaveToast] = useState(false);
  const prevPanelUpdatedRef = useRef(panelUpdated);
  const prevUpdateInProgressRef = useRef(updateInProgress);

  // Show toast when changes are saved successfully
  useEffect(() => {
    const wasUpdating = prevUpdateInProgressRef.current;
    const isUpdating = updateInProgress;
    const wasUpdated = prevPanelUpdatedRef.current;
    const isUpdated = panelUpdated;

    // Show toast when update completes successfully (was updating, now not, and panel is updated)
    if (wasUpdating && !isUpdating && isUpdated && !errors?.updateListingError && !errors?.uploadImageError) {
      setShowSaveToast(true);
    }

    prevPanelUpdatedRef.current = isUpdated;
    prevUpdateInProgressRef.current = isUpdating;
  }, [panelUpdated, updateInProgress, errors?.updateListingError, errors?.uploadImageError]);

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);
  const isPublished = listing?.id && listing?.attributes?.state !== LISTING_STATE_DRAFT;

  return (
    <div className={classes}>
      <H3 as="h1">
        {isPublished ? (
          <FormattedMessage
            id="EditListingPhotosPanel.title"
            values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
          />
        ) : (
          <FormattedMessage
            id="EditListingPhotosPanel.createListingTitle"
            values={{ lineBreak: <br /> }}
          />
        )}
      </H3>
      <EditListingPhotosForm
        className={css.form}
        disabled={disabled}
        ready={ready}
        fetchErrors={errors}
        initialValues={getInitialValues(props)}
        onImageUpload={onImageUpload}
        onSubmit={values => {
          const { addImage, ...updateValues } = values;
          onSubmit(updateValues);
        }}
        onRemoveImage={onRemoveImage}
        saveActionMsg={submitButtonText}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        listingImageConfig={listingImageConfig}
      />
      <Toast
        show={showSaveToast}
        message={intl.formatMessage({
          id: 'EditListingPhotosPanel.changesSaved',
          defaultMessage: 'Changes saved successfully.',
        })}
        onClose={() => setShowSaveToast(false)}
      />
    </div>
  );
};

export default EditListingPhotosPanel;
